import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Users, ArrowLeft, AlertTriangle } from 'lucide-react';
import Header from './Header';
import ClientsListTab from './ClientsListTab';
import BlacklistTab from './BlacklistTab';
import { useAuth } from '../contexts/AuthContext';

const ClientsList = () => {
  const { userRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Tab configuration
  const tabs = [
    {
      id: 'clients',
      name: 'Clientes',
      icon: Users,
      color: 'green',
      component: ClientsListTab
    },
    {
      id: 'blacklist',
      name: 'Lista Negra',
      icon: AlertTriangle,
      color: 'red',
      component: BlacklistTab
    }
  ];

  // Determine active tab from URL or default to 'clients'
  const getActiveTabFromPath = () => {
    const path = location.pathname;
    if (path.includes('/blacklist')) return 'blacklist';
    return 'clients';
  };

  const [activeTab, setActiveTab] = useState(getActiveTabFromPath());

  // Color mappings for tabs
  const colorMap = {
    green: {
      active: 'border-green-500 dark:border-green-400 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20',
      inactive: 'border-transparent text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:border-green-300 dark:hover:border-green-600'
    },
    red: {
      active: 'border-red-500 dark:border-red-400 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20',
      inactive: 'border-transparent text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:border-red-300 dark:hover:border-red-600'
    }
  };

  const getTabColorClasses = (tab, isActive) => {
    const colors = colorMap[tab.color] || colorMap.green;
    return isActive ? colors.active : colors.inactive;
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    
    // Update URL without page reload
    const newPath = tabId === 'clients' ? '/clients' : `/clients/${tabId}`;
    navigate(newPath, { replace: true });
  };

  // Check if user can see blacklist tab
  const canSeeBlacklist = () => {
    return userRole && ['admin', 'shopkeeper'].includes(userRole);
  };

  // Filter tabs based on permissions
  const visibleTabs = tabs.filter(tab => {
    if (tab.id === 'blacklist') {
      return canSeeBlacklist();
    }
    return true;
  });

  // Get active tab component
  const activeTabData = visibleTabs.find(tab => tab.id === activeTab);
  const ActiveComponent = activeTabData?.component;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Header />
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <Link 
            to="/"
            className="inline-flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Volver al Dashboard</span>
          </Link>
        </div>
        
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          {/* Header */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <Users className="h-6 w-6 text-green-600 dark:text-green-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Gestión de Clientes</h2>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex px-4 sm:px-6 overflow-x-auto scrollbar-hide">
              {visibleTabs.map((tab) => {
                const isActive = activeTab === tab.id;
                const Icon = tab.icon;
                
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`${getTabColorClasses(tab, isActive)} whitespace-nowrap border-b-2 py-3 sm:py-4 px-2 sm:px-3 text-xs sm:text-sm font-medium transition-colors duration-200 flex-shrink-0`}
                  >
                    <div className="flex items-center space-x-1 sm:space-x-2">
                      <Icon className="h-8 w-8 sm:h-5 sm:w-5" />
                      <span className="hidden sm:inline">{tab.name}</span>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-4 sm:p-6">
            {ActiveComponent ? (
              <ActiveComponent />
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                Componente no encontrado para la pestaña: {activeTab}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientsList;